import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Conversation, Folder, Message } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { extractErrorCode } from '../lib/dom.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { useTopBarSlot } from '../state/topbarSlot.js';
import { useChat } from '../hooks/useChat.js';
import { prefs } from '../lib/storage.js';
import { AUTO_MODEL_CHOICE_ID, resolveAutoModelId } from '../lib/modelPicker.js';
import { MessageList } from './MessageList.js';
import { Composer } from './Composer.js';
import { ShareSheet } from './ShareSheet.js';
import { hapticFeedback } from '../lib/haptics.js';
import { useOnline } from '../hooks/useOnline.js';
import { SelectionQuotePopup } from './SelectionQuotePopup.js';
import { normalizeQuote } from '../lib/messageQuote.js';
import { ShareModal } from './ShareModal.js';
import { VoiceModeOverlay } from './VoiceModeOverlay.js';
import { unlockAudio } from '../lib/audioUnlock.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { ContextMenu } from './ContextMenu.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';

const MODEL_PREF_KEY = 'lastModelChatChoice';
const LEGACY_MODEL_PREF_KEY = 'lastModelChat';

export function canShareConversation(conversationId: string | null, messagesCount: number): boolean {
  return Boolean(conversationId && messagesCount > 0);
}

export function canShowChatActions(conversationId: string | null, _messagesCount: number): boolean {
  return Boolean(conversationId);
}

export function selectedConversationMessageIds(messages: Message[], selected: Set<string>): string[] {
  return messages
    .filter((message) => selected.has(message.id) && !message.id.startsWith('local-') &&
      message.status === 'complete' && (message.role === 'user' || message.role === 'assistant') &&
      Boolean(message.content.trim()))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id))
    .map((message) => message.id);
}

export function toggleConversationMessageSelection(selected: Set<string>, id: string): Set<string> {
  if (!selected.has(id) && selected.size >= 100) return selected;
  const next = new Set(selected);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

/** The main Chat section view: message history + streaming composer. */
export function ChatScreen() {
  const { t } = useI18n();
  const { data, api, refreshModels } = useSession();
  const { activeConversationId, selectConversation, refreshConversations, toast, newChat } = useUI();
  const { el: topbarSlotEl } = useTopBarSlot();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [shareConvId, setShareConvId] = useState<string | null>(null);
  const [shareMessageId, setShareMessageId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [sheetShare, setSheetShare] = useState<{ conversationId: string; message: Message } | null>(null);
  const [prefill, setPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const online = useOnline();
  const [rewindId, setRewindId] = useState<string | null>(null);
  const [quote, setQuote] = useState<string | null>(null);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [forwardOpen, setForwardOpen] = useState(false);
  const [chatMenuPos, setChatMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [chatFolderMenuPos, setChatFolderMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [confirmDeleteChat, setConfirmDeleteChat] = useState(false);
  const [renameChatOpen, setRenameChatOpen] = useState(false);
  const [renameChatValue, setRenameChatValue] = useState('');
  const [chatActionLoading, setChatActionLoading] = useState(false);
  const lastModelsRefreshAtRef = useRef(0);

  const [selectedModelChoice, setSelectedModelChoice] = useState<string>(() => {
    const stored = prefs.get(MODEL_PREF_KEY) ?? prefs.get(LEGACY_MODEL_PREF_KEY);
    return stored ?? AUTO_MODEL_CHOICE_ID;
  });

  // Ensure selected model is valid for the chat section
  useEffect(() => {
    const models = data?.models ?? [];
    const chatModels = models.filter((m) => m.section === 'chat' && m.isAvailable && m.isActive);
    if (chatModels.length === 0) return;
    if (selectedModelChoice === AUTO_MODEL_CHOICE_ID) return;
    if (!chatModels.find((m) => m.id === selectedModelChoice)) {
      setSelectedModelChoice(AUTO_MODEL_CHOICE_ID);
      prefs.set(MODEL_PREF_KEY, AUTO_MODEL_CHOICE_ID);
    }
  }, [data?.models, selectedModelChoice]);

  const refreshModelsThrottled = useCallback(
    async (force = false) => {
      const now = Date.now();
      if (!force && now - lastModelsRefreshAtRef.current < 30_000) return;
      lastModelsRefreshAtRef.current = now;
      await refreshModels().catch(() => undefined);
    },
    [refreshModels],
  );

  const handleError = useCallback(
    (code: string) => {
      if (code === 'MODEL_UNAVAILABLE') {
        setSelectedModelChoice(AUTO_MODEL_CHOICE_ID);
        prefs.set(MODEL_PREF_KEY, AUTO_MODEL_CHOICE_ID);
        void refreshModelsThrottled(true);
      }
      const key = ('error.' + code) as Parameters<typeof t>[0];
      toast(t(key), 'error');
    },
    [refreshModelsThrottled, t, toast],
  );

  useEffect(() => {
    void refreshModelsThrottled();
    // Deduplicate burst focus+visibilitychange events (common in Telegram WebApp
    // when the mini-app comes to foreground: both events fire in the same tick).
    let burstTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleBurstRefresh = () => {
      if (burstTimer !== null) return; // already scheduled
      burstTimer = setTimeout(() => {
        burstTimer = null;
        void refreshModelsThrottled();
      }, 50);
    };
    const onFocus = () => scheduleBurstRefresh();
    const onVisibility = () => {
      if (!document.hidden) scheduleBurstRefresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      if (burstTimer !== null) clearTimeout(burstTimer);
    };
  }, [refreshModelsThrottled]);

  // Load folders for the "Add to folder" composer menu item
  useEffect(() => {
    void api.listFolders().then(({ folders: data }) => setFolders(data)).catch(() => undefined);
  }, [api]);

  useEffect(() => { setQuote(null); }, [activeConversationId]);

  useEffect(() => {
    if (!activeConversationId) {
      setActiveConversation(null);
      return;
    }
    let cancelled = false;
    void api.getConversation(activeConversationId).then((response) => {
      if (!cancelled) setActiveConversation(response.conversation);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [activeConversationId, api]);

  const handleMoveToFolder = useCallback(
    async (folderId: string | null) => {
      if (!activeConversationId) return;
      try {
        await api.updateConversation(activeConversationId, { folderId });
        setActiveConversation((prev) => (prev ? { ...prev, folderId } : prev));
        refreshConversations();
        toast(folderId ? t('menu.addToFolder') : t('sidebar.removeFromFolder'), 'success');
      } catch {
        toast(t('common.error'), 'error');
      }
    },
    [activeConversationId, api, refreshConversations, t, toast],
  );

  const handleArchiveChat = useCallback(async () => {
    if (!activeConversationId) return;
    const next = !activeConversation?.isArchived;
    setChatActionLoading(true);
    try {
      await api.updateConversation(activeConversationId, { isArchived: next });
      setActiveConversation((prev) => (prev ? { ...prev, isArchived: next } : prev));
      refreshConversations();
      toast(t(next ? 'menu.archive' : 'menu.unarchive'), 'success');
    } catch (error: unknown) {
      toast(t(('error.' + extractErrorCode(error)) as Parameters<typeof t>[0]), 'error');
    } finally {
      setChatActionLoading(false);
    }
  }, [activeConversation?.isArchived, activeConversationId, api, refreshConversations, t, toast]);

  const handleRenameChat = useCallback(async () => {
    if (!activeConversationId) return;
    const nextTitle = renameChatValue.trim();
    if (!nextTitle || nextTitle === activeConversation?.title) {
      setRenameChatOpen(false);
      return;
    }
    setChatActionLoading(true);
    try {
      const updated = await api.updateConversation(activeConversationId, { title: nextTitle });
      setActiveConversation(updated);
      refreshConversations();
      setRenameChatOpen(false);
      toast(t('menu.rename'), 'success');
    } catch (error: unknown) {
      toast(t(('error.' + extractErrorCode(error)) as Parameters<typeof t>[0]), 'error');
    } finally {
      setChatActionLoading(false);
    }
  }, [activeConversation?.title, activeConversationId, api, refreshConversations, renameChatValue, t, toast]);

  const handleConversationCreated = useCallback(
    (conversationId: string) => {
      selectConversation(conversationId);
      refreshConversations();
    },
    [refreshConversations, selectConversation],
  );

  const [voiceOpen, setVoiceOpen] = useState(false);
  const chat = useChat({
    conversationId: activeConversationId,
    section: 'chat',
    onConversationCreated: handleConversationCreated,
    onError: handleError,
  });
  useEffect(() => {
    setSelectionMode(false);
    setSelectedMessageIds(new Set());
    setForwardOpen(false);
    setShareConvId(null);
    setShareMessageId(null);
    setChatMenuPos(null);
    setChatFolderMenuPos(null);
  }, [activeConversationId]);
  const selectedIds = selectedConversationMessageIds(chat.messages, selectedMessageIds);
  function exitSelection() {
    setSelectionMode(false);
    setSelectedMessageIds(new Set());
    setForwardOpen(false);
  }
  useEffect(() => {
    if (!selectionMode || forwardOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectionMode(false);
        setSelectedMessageIds(new Set());
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectionMode, forwardOpen]);

  function handleSelectModelChoice(modelChoiceId: string) {
    setSelectedModelChoice(modelChoiceId);
    prefs.set(MODEL_PREF_KEY, modelChoiceId);
  }

  const models = data?.models ?? [];
  const resolvedModelId =
    selectedModelChoice === AUTO_MODEL_CHOICE_ID
      ? resolveAutoModelId(models)
      : selectedModelChoice;
  const canShare = canShareConversation(activeConversationId, chat.messages.length);
  const canShowActions = canShowChatActions(activeConversationId, chat.messages.length);
  const chatMenuItems = [
    ...(canShare
      ? [{
          key: 'share',
          label: t('share.button'),
          icon: 'share' as const,
          onSelect: () => {
            setShareConvId(activeConversationId);
            const lastAssistant = [...chat.messages]
              .reverse()
              .find(
                (m) =>
                  m.role === 'assistant' &&
                  m.status !== 'failed' &&
                  m.status !== 'cancelled' &&
                  Boolean(m.content?.trim()),
              );
            setShareMessageId(lastAssistant?.id ?? null);
          },
        }]
      : []),
    {
      key: 'rename',
      label: t('menu.rename'),
      icon: 'edit' as const,
      onSelect: () => {
        setRenameChatValue(activeConversation?.title ?? '');
        setRenameChatOpen(true);
      },
    },
    {
      key: 'archive',
      label: activeConversation?.isArchived ? t('menu.unarchive') : t('menu.archive'),
      icon: 'archive' as const,
      onSelect: () => {
        void handleArchiveChat();
      },
    },
    {
      key: 'addToFolder',
      label: t('menu.addToFolder'),
      icon: 'folder' as const,
      onSelect: () => {
        if (chatMenuPos) setChatFolderMenuPos(chatMenuPos);
      },
    },
    {
      key: 'delete',
      label: t('menu.delete'),
      icon: 'trash' as const,
      danger: true,
      onSelect: () => setConfirmDeleteChat(true),
    },
  ];
  const chatFolderMenuItems = [
    ...folders.map((folder) => ({
      key: folder.id,
      label: folder.name,
      icon: 'folder' as const,
      onSelect: () => {
        setChatFolderMenuPos(null);
        void handleMoveToFolder(folder.id);
      },
    })),
    ...(activeConversation?.folderId
      ? [{
          key: 'remove-from-folder',
          label: t('sidebar.removeFromFolder'),
          icon: 'close' as const,
          onSelect: () => {
            setChatFolderMenuPos(null);
            void handleMoveToFolder(null);
          },
        }]
      : []),
  ];

  const handleDeleteChat = useCallback(async () => {
    if (!activeConversationId) return;
    try {
      await api.deleteConversation(activeConversationId);
      setActiveConversation(null);
      selectConversation(null);
      refreshConversations();
      toast(t('menu.delete'), 'success');
    } catch {
      toast(t('common.error'), 'error');
    }
  }, [activeConversationId, api, refreshConversations, selectConversation, t, toast]);

  useEffect(() => {
    if (!canShowActions) {
      setChatMenuPos(null);
      setChatFolderMenuPos(null);
    }
  }, [canShowActions]);

  return (
    <div className="chat-screen">
      {selectionMode ? (
        <div className="chat-selection-toolbar" data-no-swipe role="group" aria-label={t('share.selectMessages')}>
          <button type="button" className="icon-btn chat-selection-close" aria-label={t('share.exitSelection')} onClick={exitSelection}>
            <Icon name="close" size={18} />
          </button>
          <span role="status" className="chat-selection-count">{t('share.selectedCount', { count: String(selectedIds.length) })}</span>
          <button type="button" className="icon-btn chat-selection-send" disabled={!selectedIds.length} aria-label={t('share.forwardSelected')} onClick={() => setForwardOpen(true)}>
            <Icon name="send" size={18} />
          </button>
        </div>
      ) : null}
      {!selectionMode && canShowActions && topbarSlotEl
        ? createPortal(
            <button
              type="button"
              className="icon-btn chat-screen-menu-btn"
              onClick={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                setChatMenuPos({ x: rect.right, y: rect.bottom + 4 });
              }}
              aria-label={t('topbar.menu')}
            >
              <Icon name="more" size={18} />
            </button>,
            topbarSlotEl,
          )
        : null}
      {!online ? <div className="offline-banner" role="status">{t('chat.offline')}</div> : null}
      <MessageList
        key={activeConversationId ?? 'new-conversation'}
        messages={chat.messages}
        models={models}
        loading={chat.loading}
        streaming={chat.streaming}
        thinking={chat.thinking}
        reasoningByMessageId={chat.reasoningByMessageId}
        onShare={(convId, msgId) => {
          const target = msgId ? chat.messages.find((m) => m.id === msgId) ?? null : null;
          if (target) setSheetShare({ conversationId: convId, message: target });
          else { setShareConvId(convId); setShareMessageId(msgId ?? null); }
        }}
        onRegenerate={chat.regenerate}
        onFeedback={chat.setFeedback}
        onEditMessage={chat.editMessage}
        onAttachContext={(message) => setQuote(normalizeQuote(message.content))}
        onRewind={(id) => setRewindId(id)}
        onSetChapter={chat.setChapter}
        failedSendId={chat.failedSendId}
        onRetrySend={chat.retrySend}
        onSuggestion={(text) => setPrefill({ text, nonce: Date.now() })}
        onPullNewChat={activeConversationId ? newChat : undefined}
        onSubmitQuiz={(text) => {
          if (resolvedModelId) chat.send({ text, modelId: resolvedModelId });
        }}
        activeModelId={resolvedModelId ?? ''}
        selectionMode={selectionMode}
        selectedMessageIds={new Set(selectedIds)}
        onToggleSelection={(id) => {
          if (!selectedConversationMessageIds(chat.messages, new Set([id])).length) return;
          setSelectedMessageIds((previous) => toggleConversationMessageSelection(
            new Set(selectedConversationMessageIds(chat.messages, previous)), id,
          ));
        }}
      />
      {!selectionMode ? <SelectionQuotePopup containerSelector=".message-list .bubble-content, .message-list .bubble-quote" onQuote={(text) => setQuote(normalizeQuote(text))} /> : null}
      {!selectionMode ? <Composer
        section="chat"
        conversationId={activeConversationId}
        models={models}
        selectedModelChoice={selectedModelChoice}
        resolvedModelId={resolvedModelId}
        onSelectModelChoice={handleSelectModelChoice}
        streaming={chat.streaming}
        thinking={chat.thinking}
        onSend={(text, modelId, webSearch, attachmentIds) =>
          chat.send({ text, modelId, webSearch, attachmentIds })
        }
        onStop={chat.stop}
        onVoiceMode={() => {
          unlockAudio(); // must happen inside this tap, or the browser mutes later replies
          setVoiceOpen(true);
        }}
        quote={quote}
        prefill={prefill}
        onClearQuote={() => setQuote(null)}
        folders={folders}
        onMoveToFolder={(folderId) => void handleMoveToFolder(folderId)}
      /> : null}
      <VoiceModeOverlay
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        modelId={resolvedModelId}
        models={models}
        messages={chat.messages}
        streaming={chat.streaming}
        send={chat.send}
        stop={chat.stop}
      />
      <ContextMenu
        open={chatMenuPos !== null}
        x={chatMenuPos?.x ?? 0}
        y={chatMenuPos?.y ?? 0}
        items={chatMenuItems}
        onClose={() => setChatMenuPos(null)}
      />
      <ContextMenu
        open={chatFolderMenuPos !== null}
        x={chatFolderMenuPos?.x ?? 0}
        y={chatFolderMenuPos?.y ?? 0}
        items={
          chatFolderMenuItems.length > 0
            ? chatFolderMenuItems
            : [
                {
                  key: 'no-folders',
                  label: t('common.empty'),
                  icon: 'folder',
                  onSelect: () => setChatFolderMenuPos(null),
                },
              ]
        }
        onClose={() => setChatFolderMenuPos(null)}
      />
      <ConfirmDialog
        open={rewindId !== null}
        title={t('message.rewindTitle')}
        message={t('message.rewindConfirm')}
        confirmLabel={t('message.rewind')}
        onConfirm={() => {
          const id = rewindId;
          setRewindId(null);
          if (id) void chat.rewindTo(id).catch(() => toast(t('error.generic'), 'error'));
        }}
        onCancel={() => setRewindId(null)}
      />
      <ConfirmDialog
        open={confirmDeleteChat}
        title={t('menu.deleteTitle')}
        message={t('menu.deleteConfirm')}
        onConfirm={() => {
          setConfirmDeleteChat(false);
          void handleDeleteChat();
        }}
        onCancel={() => setConfirmDeleteChat(false)}
      />
      <ShareSheet
        conversationId={sheetShare?.conversationId ?? null}
        message={sheetShare?.message ?? null}
        onClose={() => setSheetShare(null)}
        onMore={() => {
          if (!sheetShare) return;
          setShareConvId(sheetShare.conversationId);
          setShareMessageId(sheetShare.message.id);
          setSheetShare(null);
        }}
      />
      <ShareModal
        conversationId={shareConvId}
        messageId={shareMessageId}
        onSelectMessages={() => {
          setShareConvId(null);
          setShareMessageId(null);
          setSelectedMessageIds(new Set());
          setSelectionMode(true);
          hapticFeedback('tap');
        }}
        onClose={() => {
          setShareConvId(null);
          setShareMessageId(null);
        }}
      />
      <ShareModal
        conversationId={forwardOpen ? activeConversationId : null}
        messageIds={selectedIds}
        onClose={() => setForwardOpen(false)}
        onForwarded={() => {
          toast(t('share.sentSuccess'), 'success');
          exitSelection();
        }}
      />
      <Modal
        open={renameChatOpen}
        onClose={() => setRenameChatOpen(false)}
        title={t('menu.rename')}
        closeLabel={t('common.close')}
      >
        <form
          className="share-modal-body"
          onSubmit={(event) => {
            event.preventDefault();
            void handleRenameChat();
          }}
        >
          <input
            className="field"
            value={renameChatValue}
            autoFocus
            onChange={(event) => setRenameChatValue(event.target.value)}
          />
          <div className="share-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setRenameChatOpen(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={chatActionLoading || !renameChatValue.trim()}>
              {t('common.save')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
